import { Dispatch } from 'redux';
import { ChatStartersController } from '../controllers/ChatStartersController';
import LandingPageController from '../controllers/LandingPageController';
import PromoterController from '../controllers/PromoterController';
import TriggerController from '../controllers/TriggerController';
import commonHandler from '../errorHandlers/common-handler';
import {
  ChatStarterKindSnakeType,
  ChatStartersPayload,
} from '../model/ChatStarterModel';
import { selectInstagramAccountId } from '../selectors/instagramSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

type GetAllParamsType = Parameters<typeof ChatStartersController.getAll>[0];

export class ChatStartersCRUDActions extends CrudActionContainer {
  static NAME = 'CHAT_STARTERS';
  static retrieveAllSuccess = 'CHAT_STARTERS_RETRIEVED_SUCCESS';
  static invalidateAll = 'CHAT_STARTERS_INVALIDATE';
  static deleteSuccess = 'CHAT_STARTERS_DELETE_SUCCESS';

  constructor() {
    super(ChatStartersCRUDActions.NAME);
  }

  retrieveAll(params: GetAllParamsType) {
    const { botId, kind } = params;

    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const instagramAccountId = selectInstagramAccountId(getState());
      if (
        (kind === 'instagram_welcomer' ||
          kind === 'instagram_story_mention' ||
          kind === 'instagram_comment_guard') &&
        !instagramAccountId
      ) {
        return;
      }
      dispatch(this.busy(this.retrieveAllAction));

      try {
        const payload = await ChatStartersController.getAll(params);

        const retrieveAllSuccessAction: RetrievedAllSuccessActionType = {
          type: ChatStartersCRUDActions.retrieveAllSuccess,
          botId,
          kind,
          payload: {
            ...payload,
            chatStarters: payload.chatStarters,
          },
        };
        dispatch(retrieveAllSuccessAction);

        return retrieveAllSuccessAction;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  deleteByKindAndId(
    botId: number,
    kind: ChatStarterKindSnakeType,
    entityId: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        switch (kind) {
          case 'fb_comment_guard':
          case 'customer_chat':
          case 'link':
          case 'checkbox':
          case 'send_to_messenger':
          case 'optin':
          case 'instagram_welcomer':
          case 'instagram_story_mention':
          case 'instagram_comment_guard':
          case 'instagram_story_reaction':
          case 'instagram_live_comment_guard':
          case 'instagram_catch_all':
          case 'facebook_catch_all':
          case 'getemails':
            await PromoterController.deleteBotPromoterById(botId, entityId);
            break;
          case 'landing_page':
            await LandingPageController.delete(botId, entityId);
            break;
          case 'sms_keywords':
            await TriggerController.deleteTrigger(entityId);
            break;
          default:
            throw Error(`Delete for kind "${kind}" is not implemented`);
            break;
        }

        const deleteEntitySuccessAction: DeleteSuccessActionType = {
          type: ChatStartersCRUDActions.deleteSuccess,
          payload: {
            botId,
            kind,
            entityId,
          },
        };
        dispatch(deleteEntitySuccessAction);
      } catch (err) {
        return commonHandler(dispatch)(err);
      }
    };
  }

  invalidateAll() {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: ChatStartersCRUDActions.invalidateAll });
    };
  }
}

export type RetrievedAllSuccessActionType = {
  type: typeof ChatStartersCRUDActions.retrieveAllSuccess;
  botId: number;
  kind: ChatStarterKindSnakeType;
  payload: ChatStartersPayload;
};

export type DeleteSuccessActionType = {
  type: typeof ChatStartersCRUDActions.deleteSuccess;
  payload: {
    botId: number;
    kind: ChatStarterKindSnakeType;
    entityId: number;
  };
};

export const chatStartersCRUDActions = new ChatStartersCRUDActions();

export const useChatStartersCRUDActions = getActionHookBuilder(
  chatStartersCRUDActions,
  ['retrieveAll', 'invalidateAll', 'deleteByKindAndId'],
);
